'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

import { toast } from 'sonner';

const topCustomers = [
  { id: 'cust-001', name: 'Epicurean Food Hall', segment: 'Supermarket', mtdRevenue: 'XCD 384,200', outstanding: 'XCD 42,100', lastOrder: '30 Jul 2026', status: 'active' as const, trend: 8.4 },
  { id: 'cust-002', name: 'Bryson\'s Supermarket', segment: 'Supermarket', mtdRevenue: 'XCD 312,800', outstanding: 'XCD 0', lastOrder: '31 Jul 2026', status: 'active' as const, trend: 12.1 },
  { id: 'cust-003', name: 'Jolly Harbour Hotel', segment: 'Hotel', mtdRevenue: 'XCD 198,400', outstanding: 'XCD 28,400', lastOrder: '29 Jul 2026', status: 'active' as const, trend: -3.2 },
  { id: 'cust-004', name: 'Hemingways Caribbean Café', segment: 'Restaurant', mtdRevenue: 'XCD 167,300', outstanding: 'XCD 14,200', lastOrder: '28 Jul 2026', status: 'active' as const, trend: 5.7 },
  { id: 'cust-005', name: 'Couto\'s Supermarket', segment: 'Supermarket', mtdRevenue: 'XCD 143,600', outstanding: 'XCD 67,800', lastOrder: '25 Jul 2026', status: 'active' as const, trend: -1.8 },
  { id: 'cust-006', name: 'Ministry of Education AG', segment: 'Government', mtdRevenue: 'XCD 128,900', outstanding: 'XCD 128,900', lastOrder: '18 Jul 2026', status: 'active' as const, trend: 0 },
];

const lowStockAlerts = [
  { id: 'alert-001', sku: 'FRZ-0042', product: 'Chicken Wings 10kg Case', current: 12, reorder: 50, unit: 'cases', severity: 'critical' as const },
  { id: 'alert-002', sku: 'BEV-0118', product: 'Carib Beer 24-pack', current: 8, reorder: 30, unit: 'cases', severity: 'critical' as const },
  { id: 'alert-003', sku: 'DRY-0234', product: 'Canola Cooking Oil 5L', current: 24, reorder: 40, unit: 'cases', severity: 'low' as const },
  { id: 'alert-004', sku: 'DAI-0071', product: 'Nestlé Carnation Milk', current: 18, reorder: 35, unit: 'cases', severity: 'low' as const },
  { id: 'alert-005', sku: 'BEV-0204', product: 'Chubby Juice 250ml 24pk', current: 0, reorder: 25, unit: 'cases', severity: 'out' as const },
  { id: 'alert-006', sku: 'FRZ-0089', product: 'Tilapia Fillets 5kg', current: 0, reorder: 20, unit: 'cases', severity: 'out' as const },
];

const recentActivity = [
  { id: 'act-001', type: 'order', icon: 'ClipboardDocumentListIcon', message: 'Order #SO-4821 submitted — Epicurean Food Hall, XCD 28,400', time: '8m ago', color: 'text-info' },
  { id: 'act-002', type: 'delivery', icon: 'TruckIcon', message: 'Delivery #DEL-1104 completed — Bryson\'s Supermarket, 3 cases', time: '14m ago', color: 'text-positive' },
  { id: 'act-003', type: 'payment', icon: 'BanknotesIcon', message: 'Payment received — Bryson\'s Supermarket XCD 95,200 (Bank Transfer)', time: '31m ago', color: 'text-positive' },
  { id: 'act-004', type: 'alert', icon: 'ExclamationTriangleIcon', message: 'Chubby Juice 250ml reached zero stock — reorder needed', time: '47m ago', color: 'text-negative' },
  { id: 'act-005', type: 'invoice', icon: 'DocumentTextIcon', message: 'Invoice #INV-2849 sent — Jolly Harbour Hotel XCD 42,100', time: '1h 12m ago', color: 'text-primary' },
  { id: 'act-006', type: 'po', icon: 'ArrowDownTrayIcon', message: 'PO #PO-0392 approved — CaribbeanFoods Ltd XCD 184,200', time: '2h 3m ago', color: 'text-primary' },
  { id: 'act-007', type: 'quote', icon: 'DocumentDuplicateIcon', message: 'Quote #QUO-0328 approved — Hemingways Café XCD 12,800', time: '2h 44m ago', color: 'text-positive' },
  { id: 'act-008', type: 'return', icon: 'ArrowUturnLeftIcon', message: 'Return request #RET-0041 — Couto\'s Supermarket, 4 damaged cases', time: '3h 20m ago', color: 'text-warning' },
];

const segmentColors: Record<string, string> = {
  Supermarket: 'bg-info/10 text-info',
  Hotel: 'bg-accent/10 text-warning',
  Restaurant: 'bg-positive/10 text-positive',
  Government: 'bg-secondary text-secondary-foreground',
  School: 'bg-primary/10 text-primary',
  Hospital: 'bg-negative/10 text-negative',
};

const severityStyle: Record<string, string> = {
  critical: 'bg-negative/10 border-negative/30',
  low: 'bg-warning/10 border-warning/30',
  out: 'bg-negative/20 border-negative/40',
};

export default function ExecutiveBottomRow() {
  const [dismissedAlerts, setDismissedAlerts] = useState<string[]>([]);

  const handleDismissAlert = (id: string) => {
    setDismissedAlerts((prev) => [...prev, id]);
    toast.warning('Alert dismissed — item will reappear when stock changes', { duration: 3000 });
  };

  const visibleAlerts = lowStockAlerts.filter((a) => !dismissedAlerts.includes(a.id));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 xl:grid-cols-12 2xl:grid-cols-12 gap-4">

      {/* Top Customers Table — 5 cols */}
      <div className="col-span-1 lg:col-span-5 xl:col-span-5 2xl:col-span-5 bg-card rounded-xl border border-border shadow-card">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="text-sm font-700 text-foreground">Top Customers</h2>
            <p className="text-xs text-muted-foreground mt-0.5">By MTD revenue · Jul 2026</p>
          </div>
          <button className="text-xs font-600 text-primary hover:underline flex items-center gap-1">
            View all <Icon name="ArrowRightIcon" size={12} className="text-primary" />
          </button>
        </div>
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="px-5 py-2.5 text-left font-600 text-muted-foreground">Account</th>
                <th className="px-3 py-2.5 text-right font-600 text-muted-foreground">MTD Rev</th>
                <th className="px-3 py-2.5 text-right font-600 text-muted-foreground">Outstanding</th>
                <th className="px-3 py-2.5 text-right font-600 text-muted-foreground">Trend</th>
              </tr>
            </thead>
            <tbody>
              {topCustomers.map((c) => (
                <tr key={c.id} className="border-b border-border hover:bg-muted/40 transition-colors cursor-pointer group">
                  <td className="px-5 py-3">
                    <div className="font-600 text-foreground truncate max-w-[160px]">{c.name}</div>
                    <span className={`text-2xs font-600 px-1.5 py-0.5 rounded-full ${segmentColors[c.segment] ?? 'bg-muted text-muted-foreground'}`}>
                      {c.segment}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right">
                    <span className="font-mono-nums font-600 text-foreground">{c.mtdRevenue}</span>
                  </td>
                  <td className="px-3 py-3 text-right">
                    <span className={`font-mono-nums font-600 ${c.outstanding === 'XCD 0' ? 'text-positive' : parseFloat(c.outstanding.replace(/[^0-9.]/g, '')) > 50000 ? 'text-negative' : 'text-warning'}`}>
                      {c.outstanding}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right">
                    <span className={`font-mono-nums font-600 text-xs flex items-center justify-end gap-0.5 ${c.trend > 0 ? 'text-positive' : c.trend < 0 ? 'text-negative' : 'text-muted-foreground'}`}>
                      {c.trend > 0 ? <Icon name="ArrowUpIcon" size={10} className="text-positive" /> : c.trend < 0 ? <Icon name="ArrowDownIcon" size={10} className="text-negative" /> : null}
                      {c.trend !== 0 ? `${Math.abs(c.trend)}%` : '—'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Low Stock Alerts — 4 cols */}
      <div className="col-span-1 lg:col-span-4 xl:col-span-4 2xl:col-span-4 bg-card rounded-xl border border-border shadow-card">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-warning/10 flex items-center justify-center">
              <Icon name="ExclamationTriangleIcon" size={14} className="text-warning" />
            </div>
            <div>
              <h2 className="text-sm font-700 text-foreground">Stock Alerts</h2>
              <p className="text-xs text-muted-foreground">{visibleAlerts.length} items need attention</p>
            </div>
          </div>
          <button className="text-xs font-600 text-primary hover:underline">View all</button>
        </div>
        <div className="divide-y divide-border max-h-72 overflow-y-auto scrollbar-thin">
          {visibleAlerts.length === 0 ? (
            <div className="px-5 py-8 text-center">
              <Icon name="CheckCircleIcon" size={32} className="text-positive mx-auto mb-2" />
              <p className="text-sm font-600 text-foreground">All stock levels healthy</p>
              <p className="text-xs text-muted-foreground mt-1">No alerts at this time</p>
            </div>
          ) : (
            visibleAlerts.map((a) => (
              <div key={a.id} className={`flex items-center gap-3 px-4 py-3 border-l-2 ${severityStyle[a.severity]}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-600 text-foreground truncate">{a.product}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-2xs font-500 text-muted-foreground font-mono-nums">{a.sku}</span>
                    <span className={`text-2xs font-700 ${a.severity === 'out' ? 'text-negative' : 'text-warning'}`}>
                      {a.severity === 'out' ? 'OUT OF STOCK' : `${a.current} / ${a.reorder} ${a.unit}`}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    className="p-1 rounded hover:bg-primary/10 transition-colors text-primary"
                    title="Create reorder"
                    onClick={() => toast.success(`Reorder created for ${a.product}`, { duration: 3000 })}
                  >
                    <Icon name="PlusCircleIcon" size={14} className="text-primary" />
                  </button>
                  <button
                    className="p-1 rounded hover:bg-muted transition-colors"
                    title="Dismiss alert"
                    onClick={() => handleDismissAlert(a.id)}
                  >
                    <Icon name="XMarkIcon" size={14} className="text-muted-foreground" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Activity Feed — 3 cols */}
      <div className="col-span-1 lg:col-span-3 xl:col-span-3 2xl:col-span-3 bg-card rounded-xl border border-border shadow-card">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="text-sm font-700 text-foreground">Recent Activity</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Live operations feed</p>
          </div>
          <span className="flex items-center gap-1.5 text-2xs font-600 text-positive">
            <span className="w-1.5 h-1.5 bg-positive rounded-full animate-pulse" />
            Live
          </span>
        </div>
        <div className="divide-y divide-border overflow-y-auto max-h-72 scrollbar-thin">
          {recentActivity.map((a) => (
            <div key={a.id} className="flex gap-3 px-4 py-3 hover:bg-muted/40 transition-colors">
              <div className={`w-6 h-6 rounded-lg bg-muted flex items-center justify-center shrink-0 mt-0.5`}>
                <Icon name={a.icon as Parameters<typeof Icon>[0]['name']} size={13} className={a.color} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-foreground leading-snug">{a.message}</p>
                <p className="text-2xs text-muted-foreground mt-0.5">{a.time}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="px-5 py-3 border-t border-border">
          <button className="text-xs font-600 text-primary hover:underline w-full text-center">
            View full audit log →
          </button>
        </div>
      </div>
    </div>
  );
}